import store from '@/store';
import {
    ActivityProduct,
    Cart,
    Customer,
    FerryProduct,
    FlightProduct,
    HotelProduct,
    ProductType,
} from '@/api/Cart/cart.interfaces';
import { AnyProduct } from '@shared/interfaces/product';
import { AgeRangeDefinitions } from '@/api/Checkout/checkout.interfaces';
import { copyProductPax } from '@/helpers/cartCopy';
import { getAgeRange, isValidSessionCart } from '@/api/Cart/cart.helpers';
import { generateUUID } from '@shared/utils/uuid';
import { logStatsigCheckoutCartSessionStore } from '@/tracking/statsig/checkout.functions';
import { configBase } from '@/config/base';

const STORAGE_KEY = 'previous_cart';
const supportedCopyProducts = new Set([
    ProductType.Flight,
    ProductType.Ferry,
    ProductType.Hotel,
    ProductType.Activity,
]);

export function removeSessionCart() {
    sessionStorage.removeItem(STORAGE_KEY);
}

export function saveCartToSession(cart?: Cart | null): void {
    if (!configBase.sessionCartStorage.enabled) {
        return;
    }
    const isValidCart = cart && isValidSessionCart(cart);

    if (isValidCart) {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
            logStatsigCheckoutCartSessionStore(cart, true);
        } catch (error) {
            logStatsigCheckoutCartSessionStore(cart, false);
            console.error('Failed to save cart to sessionStorage:', error);
        }
    }
}

function loadCartFromSession(): Cart | null {
    const storedCart = sessionStorage.getItem(STORAGE_KEY);

    if (storedCart) {
        try {
            const parsedCart = JSON.parse(storedCart);
            return parsedCart as Cart;
        } catch (error) {
            console.error('Failed to parse cart from sessionStorage:', error);
        }
    }

    return null;
}

export function canSetInitialValues(cart: Cart): boolean {
    const previousCart = loadCartFromSession();

    if (previousCart) {
        return hasEqualProducts(cart, previousCart);
    }
    return false;
}

export function updatedValuesFromPreviousCart(cart: Cart): Cart {
    const previousCart = loadCartFromSession();
    if (previousCart && canSetInitialValues(cart)) {
        const products = getUpdatedProducts(cart, previousCart);
        const addAgency = Boolean(cart.Agency && previousCart.Agency);
        const customer = getUpdatedCustomer(cart.Customer, previousCart.Customer);

        return {
            ...cart,
            Products: products,
            Customer: customer,
            ...(addAgency && { Agency: previousCart.Agency }),
        };
    }
    return cart;
}

function getUpdatedCustomer(customer: Customer, oldCustomer: Customer): Customer {
    return {
        ...customer,
        Email: oldCustomer.Email ?? customer.Email,
        PhoneNumber: oldCustomer.PhoneNumber ?? customer.PhoneNumber,
        CountryCode: oldCustomer.CountryCode ?? customer.CountryCode,
    };
}

function getUpdatedProducts(cart: Cart, oldCart: Cart): AnyProduct[] {
    const usedProducts = new Set<string>();
    const newProducts: AnyProduct[] = [];
    cart.Products.forEach((product) => {
        if (!supportedCopyProducts.has(product.Type)) {
            newProducts.push(product);
        } else {
            const sourceProduct = oldCart.Products.find(
                (p) =>
                    getProductAndPaxHash(p) === getProductAndPaxHash(product) &&
                    !usedProducts.has(p.Id)
            );
            if (sourceProduct) {
                usedProducts.add(sourceProduct.Id);
                newProducts.push(copyProductPax(sourceProduct, product));
            } else {
                newProducts.push(product);
            }
        }
    });
    return newProducts;
}

function hasEqualProducts(c1: Cart, c2: Cart): boolean {
    const getHash = (cart: Cart) =>
        cart.Products.filter((p) => supportedCopyProducts.has(p.Type))
            .map(getProductAndPaxHash)
            .sort()
            .join(';');

    const c1Hash = getHash(c1);
    const c2Hash = getHash(c2);

    return c1Hash === c2Hash;
}

function getProductAndPaxHash(product: AnyProduct) {
    const ageRangeDefinitions: AgeRangeDefinitions =
        store.getters['content/getCheckoutAgeRangeDefinitions'];
    const getPaxHash = (p: AnyProduct) => {
        switch (p.Type) {
            case ProductType.Flight: {
                const product = p as FlightProduct;
                return product.Pax.map(
                    (pax) => getAgeRange(product, pax, ageRangeDefinitions).Type
                )
                    .sort()
                    .join(',');
            }
            case ProductType.Ferry: {
                const product = p as FerryProduct;
                return product.Pax.map(
                    (pax) => getAgeRange(product, pax, ageRangeDefinitions).Type
                )
                    .sort()
                    .join(',');
            }
            case ProductType.Hotel: {
                const product = p as HotelProduct;
                return product.Rooms.map(
                    (room) => getAgeRange(product, room.Pax, ageRangeDefinitions).Type
                )
                    .sort()
                    .join(',');
            }
            case ProductType.Activity: {
                const product = p as ActivityProduct;
                return product.Tickets.map(
                    (ticket) => getAgeRange(product, ticket.Pax, ageRangeDefinitions).Type
                )
                    .sort()
                    .join(',');
            }
            default:
                return generateUUID();
        }
    };

    return `${product.Type}|${getPaxHash(product)}`;
}
